import Pill from '@pelckmans/elementis/components/pill';
import CalendarIcon from '@pelckmans/elementis/icons/calendar';
import CrossIcon from '@pelckmans/elementis/icons/failure';
import InfoIcon from '@pelckmans/elementis/icons/info';
import TimeIcon from '@pelckmans/elementis/icons/time';
import CheckIcon from '@pelckmans/elementis/icons/success';
import TransferIcon from '@pelckmans/elementis/icons/swap';

import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../../utils/formatDate';
import { License, LICENSE_TRANSFER_DIRECTION, LicenseTransfer } from '../../api/licenses';

const BASE = 'PAGES.PROFILE.LICENSES';

type LicenseState = 'active' | 'expired' | 'pending';
export function getLicenseState(license: License, licenseTransfers: LicenseTransfer[]): LicenseState {
  const isPendingTransfer = licenseTransfers.some(transfer => transfer.license.id === license.id);

  if (isPendingTransfer) {
    return 'pending';
  }

  return new Date(license.validTo) > new Date() ? 'active' : 'expired';
}

function getLicenseStateProminence(state: LicenseState): 'success' | 'danger' | 'info' {
  switch (state) {
    case 'active':
      return 'success';
    case 'expired':
      return 'danger';
    default:
      return 'info';
  }
}

function getLicenseStateIcon(state: LicenseState) {
  switch (state) {
    case 'active':
      return <CheckIcon />;
    case 'expired':
      return <CrossIcon />;
    case 'pending':
      return <TimeIcon />;
    default:
      return <InfoIcon />;
  }
}

export function LicenseStatePill({
  license,
  size,
  licenseTransfers,
}: {
  license: License;
  size?: ComponentProps<typeof Pill>['size'];
  licenseTransfers: LicenseTransfer[];
}) {
  const { t } = useTranslation();
  const state = getLicenseState(license, licenseTransfers);
  return (
    <Pill size={size} icon={getLicenseStateIcon(state)} prominence={getLicenseStateProminence(state)}>
      {t(`${BASE}.MY_LICENSES.STATE.${state.toUpperCase()}`)}
    </Pill>
  );
}

export function LicenseTransferPill({
  license,
  size,
  licenseTransfers,
}: {
  license: License;
  size?: ComponentProps<typeof Pill>['size'];
  licenseTransfers: LicenseTransfer[];
}) {
  const { t } = useTranslation();

  const transfer = licenseTransfers.find(transfer => transfer.license.id === license.id);

  if (!transfer) return null;

  return (
    <Pill size={size} icon={<TransferIcon />} prominence="info">
      {t(`${BASE}.MY_LICENSES.TRANSFER.${transfer.direction === LICENSE_TRANSFER_DIRECTION.SENDER ? 'TO' : 'FROM'}`, {
        email: transfer?.email,
      })}
    </Pill>
  );
}

export function LicenseValidityPill({
  license,
  size,
}: {
  license: License;
  size?: ComponentProps<typeof Pill>['size'];
}) {
  const { t } = useTranslation();
  return (
    <Pill size={size} icon={<CalendarIcon />} prominence="neutral">
      {t('PAGES.PROFILE.LICENSES.MY_LICENSES.VALIDITY', { validTo: formatDate(license.validTo) })}
    </Pill>
  );
}

export function LicenseSubjectPill({
  license,
  size,
}: {
  license: License;
  size?: ComponentProps<typeof Pill>['size'];
}) {
  if (!license.subject) return null;

  return (
    <Pill size={size} prominence="neutral">
      {license.subject}
    </Pill>
  );
}
